import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { AppNotification } from '../../shared/api/types';
import { onLive } from '../../shared/live';
import { enablePush, isPushOn, pushSupport } from '../../shared/push/push';

/** Avisos que se muestran encima de todo: una solicitud de asistencia o la invitación al control. */
const URGENT = new Set(['presentation.assist', 'presentation.remote']);
const WINDOW_MS = 15 * 60_000;

const urgent = (items: AppNotification[]) => items.filter((n) => URGENT.has(n.kind) && !n.readAt && !n.resolvedAt && Date.now() - new Date(n.createdAt).getTime() < WINDOW_MS).sort((a, b) => a.createdAt.localeCompare(b.createdAt));

/**
 * Alerta roja a pantalla completa cuando llega una solicitud de asistencia (o el enlace al control
 * por celular) con KEORA abierta: el mensaje grande, el ministerio, quién y a qué hora, y «Enterado».
 * Llega al instante por el canal en vivo (y una consulta cada 10 s de respaldo); también al abrir
 * KEORA si hay una sin atender de los últimos 15 minutos. Si llegan varias, se muestran en fila.
 */
export function UrgentAlert() {
  const [queue, setQueue] = useState<AppNotification[]>([]);
  const [push, setPush] = useState<{ key: string; on: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const shown = useRef<string | null>(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    void api
      .get<{ items: AppNotification[] }>('/notifications/me')
      .then((r) => setQueue(urgent(r.items)))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    load();
    const off = onLive((e) => {
      if (e.type === 'notification' && URGENT.has(e.kind)) load();
    });
    const t = setInterval(() => document.visibilityState === 'visible' && load(), 10_000);
    const onVisible = () => document.visibilityState === 'visible' && load();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      off();
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  const current = queue[0] ?? null;

  // Vibra cuando aparece una nueva; y revisa si este celular recibe los avisos con la app cerrada.
  useEffect(() => {
    if (!current || shown.current === current.id) return;
    shown.current = current.id;
    navigator.vibrate?.([400, 150, 400, 150, 400]);
    void api
      .get<{ publicKey: string | null }>('/notifications/push/key')
      .then(async ({ publicKey }) => (publicKey && pushSupport(publicKey) === 'ok' ? setPush({ key: publicKey, on: await isPushOn().catch(() => false) }) : setPush(null)))
      .catch(() => setPush(null));
  }, [current]);

  if (!current) return null;
  const assist = current.kind === 'presentation.assist';
  const [ministry, ...rest] = current.title.split(': ');
  const message = assist && rest.length > 0 ? rest.join(': ') : current.title;

  async function done(open: boolean) {
    if (!current) return;
    setError(null);
    setQueue((q) => q.filter((n) => n.id !== current.id));
    await api.post(`/notifications/me/${current.id}/read`, {}).catch(() => undefined);
    if (open && current.link) navigate(current.link);
  }

  async function turnOnPush() {
    if (!push) return;
    try {
      await enablePush(push.key);
      setPush({ ...push, on: true });
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  return (
    <div className="urgent" role="alertdialog" aria-modal="true" aria-label={assist ? `Solicitud para ${ministry}` : 'Control desde el celular'}>
      {assist ? <p className="urgent__ministry">{ministry}</p> : <p className="urgent__ministry">Control desde el celular</p>}
      <p className="urgent__message">{assist ? message : current.title.replace(/^Control desde el celular: /, '')}</p>
      {current.body ? <p className="urgent__who">{current.body}</p> : null}
      {queue.length > 1 ? <p className="urgent__more">{queue.length - 1} más en espera</p> : null}
      <div className="urgent__actions">
        {assist ? (
          <button type="button" className="urgent__ok" onClick={() => void done(false)}>
            Enterado
          </button>
        ) : (
          <>
            <button type="button" className="urgent__ok" onClick={() => void done(true)}>
              Abrir control
            </button>
            <button type="button" className="urgent__later" onClick={() => void done(false)}>
              Cerrar
            </button>
          </>
        )}
      </div>
      {push && !push.on ? (
        <button type="button" className="urgent__push" onClick={() => void turnOnPush()}>
          Activar notificaciones en este celular (para que lleguen aunque KEORA esté cerrada)
        </button>
      ) : null}
      {error ? <p className="urgent__error">{error}</p> : null}
    </div>
  );
}
